import React, { useEffect, useRef } from "react";

const BOOK_COVER = "/assets/book-cover.webp";

/**
 * BookCard3D — premium parallax tilt for the Authentic Momentum book cover.
 *
 * Design choices:
 * - On fine-pointer devices, the card listens for mousemove and tilts
 *   toward the cursor. Movement is dampened (max ±10°) so the cover stays
 *   readable; rotation is GPU-accelerated via `transform: perspective()
 *   rotateX/Y`.
 * - On coarse-pointer / mobile, we fall back to a slow idle breathing
 *   tilt so the book still feels alive without draining battery on
 *   continuous touchmove handlers.
 * - `prefers-reduced-motion` disables all motion — the cover renders as
 *   a static, well-lit object.
 * - Drawn-in gold edge highlight + soft floor shadow add tangible weight
 *   without obscuring the cover art.
 */
const BookCard3D = () => {
  const rootRef = useRef(null);
  const innerRef = useRef(null);
  const stateRef = useRef({
    targetX: 0,
    targetY: 0,
    currX: 0,
    currY: 0,
    raf: 0,
    idle: 0,
    visible: false,
  });

  useEffect(() => {
    const fine = window.matchMedia("(pointer: fine)").matches;
    const noMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const root = rootRef.current;
    const inner = innerRef.current;
    if (!root || !inner) return undefined;
    if (noMotion) {
      inner.style.transform = "perspective(1100px) rotateX(0deg) rotateY(0deg)";
      return undefined;
    }

    const MAX_TILT_DEG = 9;
    const LERP = 0.08;

    const handleMove = (e) => {
      const rect = root.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width - 0.5; // -0.5..0.5
      const y = (e.clientY - rect.top) / rect.height - 0.5;
      stateRef.current.targetX = -y * MAX_TILT_DEG * 2; // rotateX (vertical mouse → x-axis)
      stateRef.current.targetY = x * MAX_TILT_DEG * 2; // rotateY
    };

    const handleLeave = () => {
      stateRef.current.targetX = 0;
      stateRef.current.targetY = 0;
    };

    if (fine) {
      root.addEventListener("mousemove", handleMove);
      root.addEventListener("mouseleave", handleLeave);
    }

    const tick = () => {
      const s = stateRef.current;
      if (!s.visible) return;
      // For coarse-pointer devices we add a gentle idle sway so the
      // book breathes even without input.
      if (!fine) {
        s.idle += 0.012;
        // Lighter idle on coarse pointer (mobile) so it feels alive
        // but never distracting. Was ±3° / ±4° — now ±1.6° / ±2°.
        s.targetX = Math.sin(s.idle * 0.7) * 1.6;
        s.targetY = Math.cos(s.idle * 0.55) * 2;
      }
      s.currX += (s.targetX - s.currX) * LERP;
      s.currY += (s.targetY - s.currY) * LERP;
      inner.style.transform = `perspective(1100px) rotateX(${s.currX.toFixed(2)}deg) rotateY(${s.currY.toFixed(2)}deg)`;
      // Move the gold highlight to follow the cursor for a subtle
      // light-on-cover effect.
      const lx = 50 + s.currY * 1.5;
      const ly = 50 - s.currX * 1.5;
      inner.style.setProperty("--lx", `${lx}%`);
      inner.style.setProperty("--ly", `${ly}%`);
      s.raf = requestAnimationFrame(tick);
    };
    // Animate only while the card is on screen so the tilt loop never
    // competes with scrolling elsewhere on the page.
    const io = new IntersectionObserver(([entry]) => {
      const s = stateRef.current;
      const nowVisible = entry.isIntersecting;
      if (nowVisible && !s.visible) {
        s.visible = true;
        s.raf = requestAnimationFrame(tick);
      } else if (!nowVisible) {
        s.visible = false;
        cancelAnimationFrame(s.raf);
      }
    });
    io.observe(root);

    // Capture the ref's current object so the cleanup function can cancel
    // the RAF safely even if `stateRef.current` is reassigned later.
    const stateAtMount = stateRef.current;
    return () => {
      cancelAnimationFrame(stateAtMount.raf);
      io.disconnect();
      root.removeEventListener("mousemove", handleMove);
      root.removeEventListener("mouseleave", handleLeave);
    };
  }, []);

  return (
    <div
      ref={rootRef}
      data-testid="book-3d-card"
      className="book3d-root relative mx-auto max-w-md"
    >
      <div className="book3d-floor" aria-hidden />
      <div ref={innerRef} className="book3d-inner">
        <div className="book3d-glow" aria-hidden />
        <img
          src={BOOK_COVER}
          alt="Authentic Momentum book cover"
          data-testid="book-cover-image"
          className="book3d-cover"
          draggable="false"
          loading="lazy"
          decoding="async"
        />
        <div className="book3d-edge" aria-hidden />
        <div className="book3d-highlight" aria-hidden />
      </div>
    </div>
  );
};

export default BookCard3D;
